/**
 * Margin selectors — pure reads over the §5 /resolve trust envelope (Sage ledger
 * lineage). Replaces the old representative view-model: every number the Margin
 * surface shows is now a served Cell, so a value never renders without its envelope.
 *
 * Two axes are genuinely additive and reconcile by construction against the master:
 *  - site: Group = Kirkstall + Oracle (the SUM group_rule metrics) — the Group→Entity
 *    drill-through the surface exposes, checked here on the served cells.
 *  - the P&L ladder (Revenue → −COGS → Gross Profit → −OPEX → EBITDA) is the real
 *    Sage bridge — no fabricated floor/job-cost leg (there is no trustworthy line cost).
 * Below entity there is no GL-line grain (Sage serves account-group balances), so the
 * surface says so honestly rather than inventing a breakdown.
 */

import { isPeriodComplete, periodHeading } from '../components/kit/cellFormat'
import type { Cell, PlanType, ResolveResponse, Site } from '../types/metrics'

/** The P&L ladder rows — `total` anchors, `less` deductions. All Sage-served. */
export const PNL_LADDER: { id: string; label: string; kind: 'total' | 'less' }[] = [
  { id: 'revenue', label: 'Revenue', kind: 'total' },
  { id: 'cogs', label: 'COGS', kind: 'less' },
  { id: 'gross_profit', label: 'Gross Profit', kind: 'total' },
  { id: 'opex', label: 'OPEX', kind: 'less' },
  { id: 'ebitda', label: 'EBITDA', kind: 'total' },
]

/** The two entities that compose the Group (site axis). */
export const COMPOSITION_SITES: readonly Site[] = ['Kirkstall', 'Oracle']

const TOLERANCE = 0.005 // §5 sum_check numeric rule, mirrored on the served cells

const keyOf = (metricId: string, site: string, period: string, plan: string) =>
  `${metricId}|${site}|${period}|${plan}`

/** One lookup for the whole window so every selector is an O(1) read. */
export function cellLookup(resolve: ResolveResponse): Map<string, Cell> {
  const lookup = new Map<string, Cell>()
  for (const cell of resolve.cells) {
    lookup.set(keyOf(cell.metric_id, cell.site, cell.period, cell.plan_type), cell)
  }
  return lookup
}

export function pick(
  lookup: Map<string, Cell>,
  metricId: string,
  site: Site,
  period: string,
  plan: PlanType = 'Actual'
): Cell | null {
  return lookup.get(keyOf(metricId, site, period, plan)) ?? null
}


/** The pseudo-period the window aggregate is keyed at. It is not a Sage month, so it can
 * never collide with one inside the same lookup. */
export const WINDOW_PERIOD = 'window'

/** Ratios are RE-DERIVED from their summed parts, never averaged: the mean of 8 monthly
 * percentages is not the percentage of the 8 months. This mirrors the master, which derives
 * GPM from Revenue and COGS per month (registry: gpm carries group_rule `derived`). */
const DERIVED_FROM: Record<string, { numerator: string; denominator: string }> = {
  gpm: { numerator: 'gross_profit', denominator: 'revenue' },
}

export interface WindowSummary {
  /** Every cell this window can answer for: the SERVED MONTHS at their own period ids, and
   * the aggregate at WINDOW_PERIOD. The two sets cannot collide — WINDOW_PERIOD is not a date
   * — so one map answers both "what did July post" and "what does the window total".
   *
   * ⚠ It carried the aggregate ALONE until 24 Aug 2026, which is why a month-grained reader
   * (MarginBridge) got an em-dash on every Sage column while the total row read correctly. */
  lookup: Map<string, Cell>
  /** What the aggregate covers, for a header: 'Jul 26' or 'Jan 26 → Jul 26'. */
  label: string
  /** The CLOSED months the totals are made of, in order. */
  months: string[]
  /** Served months that have NOT ended, held OUT of the totals.
   *
   * ⚠ NOTHING RENDERS THIS TODAY (24 Aug 2026). The P&L bridge used to list it and no longer
   * does — with a whole-year scope it printed five future months a reader already had from the
   * card's own title. The EXCLUSION is what matters and that is unchanged; this stays because
   * "what did the window drop" is part of what windowSummary means, and it is tested. */
  openMonths: string[]
}

/** The served Actual cells for one metric and site, in period order. */
function monthsOf(byPeriod: Map<string, Cell>, periods: string[], metricId: string, site: string): Cell[] {
  return periods
    .map((period) => byPeriod.get(keyOf(metricId, site, period, 'Actual')))
    .filter((cell): cell is Cell => cell !== undefined)
}

/**
 * One aggregate Cell from the served months. The envelope travels with the sum: it carries
 * the DQ flags of every part, and a lock state no stronger than its weakest month — one live
 * month makes the whole total live. RAG goes to `none`, because a sum has no served target to
 * measure against and a colour we cannot defend is worse than no colour at all.
 */
function sumCell(cells: Cell[]): Cell {
  const served = cells.filter((cell) => cell.value !== null)
  const locks = new Set(cells.map((cell) => cell.lock_state))
  const allLocked = locks.size === 1 && locks.has('locked')
  return {
    ...cells[cells.length - 1],
    period: WINDOW_PERIOD,
    value: served.length ? served.reduce((total, cell) => total + (cell.value as number), 0) : null,
    ytd_value: null,
    rag: 'none',
    lock_state: locks.has('divergent') ? 'divergent' : allLocked ? 'locked' : 'live',
    dq_flags: [...new Set(cells.flatMap((cell) => cell.dq_flags))],
  }
}

/** Every (metric, site) pair this response served an Actual for. */
function servedPairs(resolve: ResolveResponse): { metricId: string; site: string }[] {
  const pairs = new Map<string, { metricId: string; site: string }>()
  for (const cell of resolve.cells) {
    if (cell.plan_type === 'Actual') pairs.set(`${cell.metric_id}|${cell.site}`, { metricId: cell.metric_id, site: cell.site })
  }
  return [...pairs.values()]
}

/**
 * Aggregate the whole served window into cells keyed at WINDOW_PERIOD.
 *
 * ⚠ WHY THIS REPLACED A SINGLE ANCHOR MONTH. The surface used to headline one month while
 * the scope bar named a year, so the page reported two periods at once. Every panel now
 * reads the window the user scoped to.
 *
 * A metric aggregates by its OWN registry rule, carried on the served cell: `sum` sums,
 * `derived` re-derives from the summed parts, and anything else gets no window cell — so the
 * surface shows the em-dash rather than a number nobody can defend. Actual only: a summed
 * Target is not a served plan.
 *
 * ⚠⚠ CLOSED MONTHS ONLY, AND IT IS WHAT MAKES THE LADDER A BRIDGE (23 Aug 2026). The five
 * legs do not all stop in the same place: `revenue` blends a FORECAST into every unclosed
 * month (deliberate, and the cell carries a dq_flag saying so), while COGS, Gross Profit,
 * OPEX and EBITDA carry none and stop at the last posted month. Summing a mixed window added
 * FIVE months of forecast revenue to EIGHT of actual cost — Group 2026 read Revenue
 * £8,862,370 against a served Gross Profit of £1,680,365, a £4,517,098 hole and GPM 19.0%.
 * The arithmetic was right; the POPULATION was wrong. Over the closed months the same cells
 * tie to the penny: £4,054,848 − £2,637,329 = £1,417,519, GPM 35.0%.
 *
 * An open month is therefore excluded and NAMED (windowSummary.openMonths), never blended.
 * It is deliberately the same rule /margin applies to the invoice fact — two definitions of
 * "closed" on one page is how the page stopped tying in the first place.
 */
export function aggregateWindow(resolve: ResolveResponse, now: Date = new Date()): Map<string, Cell> {
  const byPeriod = cellLookup(resolve)
  const closed = resolve.periods.filter((period) => isPeriodComplete(period, now))
  const lookup = new Map<string, Cell>()
  for (const { metricId, site } of servedPairs(resolve)) {
    const months = monthsOf(byPeriod, closed, metricId, site)
    if (months.length && months[0].group_rule === 'sum') {
      lookup.set(keyOf(metricId, site, WINDOW_PERIOD, 'Actual'), sumCell(months))
    }
  }
  for (const { metricId, site } of servedPairs(resolve)) {
    const rule = DERIVED_FROM[metricId]
    const template = monthsOf(byPeriod, closed, metricId, site).pop()
    if (!rule || !template) continue
    const top = lookup.get(keyOf(rule.numerator, site, WINDOW_PERIOD, 'Actual'))?.value ?? null
    const bottom = lookup.get(keyOf(rule.denominator, site, WINDOW_PERIOD, 'Actual'))?.value ?? null
    const value = top !== null && bottom !== null && bottom !== 0 ? top / bottom : null
    lookup.set(keyOf(metricId, site, WINDOW_PERIOD, 'Actual'), { ...template, period: WINDOW_PERIOD, value, ytd_value: null, rag: 'none' })
  }
  return lookup
}

/** The window aggregate plus what it is made of — the CLOSED months the totals cover, and
 * the open ones held out of them. An open month is named rather than blended: on the revenue
 * leg it is a forecast, and adding a forecast to seven months of actual cost is what broke
 * the bridge (see aggregateWindow). */
export function windowSummary(
  resolve: ResolveResponse,
  site: Site,
  anchorId = 'revenue',
  now: Date = new Date()
): WindowSummary {
  const byPeriod = cellLookup(resolve)
  const served = resolve.periods.filter((period) => byPeriod.get(keyOf(anchorId, site, period, 'Actual'))?.value != null)
  const months = served.filter((period) => isPeriodComplete(period, now))
  // The label is READ, so it carries the platform's month format ('Jan 26'), never the served
  // id. The id stays the key, and `months` keeps it for anything that needs to address a month.
  const span = months.length > 1
    ? `${periodHeading(months[0])} → ${periodHeading(months[months.length - 1])}`
    : months.length ? periodHeading(months[0]) : ''
  return {
    // Served months first, aggregate second. Distinct key spaces, so the spread cannot
    // shadow either one — see WindowSummary.lookup.
    lookup: new Map([...byPeriod, ...aggregateWindow(resolve, now)]),
    label: span,
    months,
    openMonths: served.filter((period) => !isPeriodComplete(period, now)),
  }
}

export interface SitePart {
  site: Site
  cell: Cell | null
}

export interface SiteReconcile {
  group: Cell | null
  parts: SitePart[]
  sum: number
  reconciles: boolean
}

/** The Group→Entity split for a metric at one period, with the served-cell sum check. */
export function siteReconcile(
  lookup: Map<string, Cell>,
  metricId: string,
  period: string
): SiteReconcile {
  const group = pick(lookup, metricId, 'Group', period)
  const parts = COMPOSITION_SITES.map((site) => ({ site, cell: pick(lookup, metricId, site, period) }))
  const sum = parts.reduce((acc, part) => acc + (part.cell?.value ?? 0), 0)
  const reconciles = group?.value != null && Math.abs(group.value - sum) < TOLERANCE
  return { group, parts, sum, reconciles }
}
